import { t } from "@travily/i18n";
import familyIllustration from "@ui/assets/illustrations/family-animated.webp";
import { RadioGroupWithFollowUp } from "@ui/components/RadioGroupWithFollowUp";
import { ResponsiveText } from "@ui/components/ResponsiveText";
import { Wizard } from "@ui/components/Wizard";
import { useBreakpoint } from "@ui/hooks/use-breakpoint";
import type { ReactNode } from "react";

const COLOR = "red" as const;

export interface FoundingMemberFamilyHubsStepViewProps {
  /** Selected value for the "have you experienced hubs" radio ("yes" | "no" | null). */
  experienceValue: string | null;
  /** Free-text follow-up capturing which hubs the family has visited. */
  visitedValue: string;
  /** Validation error for the experience field, when present. */
  error?: string;
  /** 1-indexed current wizard stage. */
  currentStage: number;
  /** Total number of wizard stages. */
  totalStages: number;
  /** Whether the primary (continue) button is pending. */
  primaryPending: boolean;
  /** Top-bar tertiary actions (Questions / Save & finish later) rendered inside the wizard. */
  topBarActions: ReactNode;
  /** Back navigation handler; omitted on the first stage. */
  onBack?: () => void;
  /** Primary/continue submit handler. */
  onPrimary: () => void;
  /** Called with the new radio value when the experience selection changes. */
  onExperienceChange: (value: string) => void;
  /** Called with the new text when the visited follow-up changes. */
  onVisitedChange: (value: string) => void;
}

export const FoundingMemberFamilyHubsStepView = ({
  experienceValue,
  visitedValue,
  error,
  currentStage,
  totalStages,
  primaryPending,
  topBarActions,
  onBack,
  onPrimary,
  onExperienceChange,
  onVisitedChange,
}: FoundingMemberFamilyHubsStepViewProps) => {
  const isAboveMd = useBreakpoint("md");
  const isAboveLg = useBreakpoint("lg");
  const selectionGroupSize = isAboveLg ? "5xl" : isAboveMd ? "4xl" : "3xl";

  const items = [
    {
      value: "yes",
      label: (
        <ResponsiveText
          base={t("web.founding_members.creator.steps.family.hubs.show.options.true.base")}
          md={t("web.founding_members.creator.steps.family.hubs.show.options.true.md")}
        />
      ),
    },
    {
      value: "no",
      label: (
        <ResponsiveText
          base={t("web.founding_members.creator.steps.family.hubs.show.options.false.base")}
          md={t("web.founding_members.creator.steps.family.hubs.show.options.false.md")}
        />
      ),
    },
  ];

  return (
    <Wizard color={COLOR} currentStage={currentStage} totalStages={totalStages} onBack={onBack}>
      {topBarActions}
      <Wizard.PrimaryButton onPress={onPrimary} isPending={primaryPending}>
        {t("common.actions.continue")}
      </Wizard.PrimaryButton>
      <Wizard.Stage
        illustration={<img alt="" aria-hidden="true" src={familyIllustration} />}
        heading={t("web.founding_members.creator.steps.family.hubs.show.title")}
      >
        <div className="w-full max-w-[600px]">
          <RadioGroupWithFollowUp
            align="horizontal"
            color={COLOR}
            followUp={{
              triggerValue: "yes",
              presentation: "card",
              label: t("web.founding_members.creator.steps.family.hubs.show.follow_up.label"),
              placeholder: t("web.founding_members.creator.steps.family.hubs.show.follow_up.placeholder"),
              value: visitedValue,
              onChange: onVisitedChange,
            }}
            items={items}
            onChange={onExperienceChange}
            size={selectionGroupSize}
            value={experienceValue ?? undefined}
          />
          {error ? <Wizard.FieldError>{error}</Wizard.FieldError> : null}
        </div>
      </Wizard.Stage>
    </Wizard>
  );
};
